/**
 * Import Monitor — live ERP shortage report.
 *
 * The ERP is the source of truth: overdue schedule lines the ERP sent in its
 * latest complete PO push that are short in the ERP (or in Helix only), each
 * with how Helix lines up and whether it needs repair on the Helix side.
 * Only tenants whose import captures the ERP balance return `enabled: true`;
 * `push.available` is false until a complete push is on record.
 */
import api from './client';

/** How Helix lines up with the ERP for one schedule line. */
export type ErpShortageAlignment =
  | 'aligned'
  | 'accepted'
  | 'helix_received_more'
  | 'helix_missing_receipts'
  | 'cancelled_in_helix'
  | 'short_in_helix_only';
/** Every line (''), the ERP's own report, everything needing repair, next-day ERP duplicate pairs, or one alignment. */
export type ErpShortageView = 'erp_shortage' | 'needs_repair' | 'next_day_duplicates' | ErpShortageAlignment;
/** Days past the schedule date. */
export type ErpShortageOverdue = '1_7' | '8_14' | '15_30' | '30_plus';
export type ErpShortageOrdering = 'overdue' | 'recent' | 'difference' | 'erp_short' | 'supplier';

/** An accepted difference on the line. Inactive once the figures have moved on. */
export interface ErpShortageException {
  /** Still holds: Helix's status and balance and the ERP's balance are unchanged since acceptance. */
  active: boolean;
  reason: string;
  accepted_by: string | null;
  accepted_at: string;
}

/**
 * A line's part in a next-day ERP duplicate pair: the ERP repeated the slot the
 * next day with the same qty and booked the receipt against the repeat.
 */
export interface ErpNextDayDuplicateMark {
  /** `earlier`: Helix received it, the ERP still shows it open. `next_day`: the ERP received it, Helix shows it open. */
  role: 'earlier' | 'next_day';
  /** Identifies the pair — the id a fix is requested with. */
  earlier_schedule_id: number;
  partner_schedule_id: number;
  partner_schedule_line_number: string | null;
  partner_date: string;
}

export interface ErpShortageRow {
  schedule_id: number;
  po_line_id: number;
  po_number: string;
  line_number: number;
  schedule_line_number: string | null;
  /** Raw ERP order type of the PO (e.g. 1 = standard / spot PO, 4 = scheduling agreement). */
  erp_order_type: string | null;
  part_number: string | null;
  description: string | null;
  supplier_id: number | null;
  supplier_name: string | null;
  supplier_code: string | null;
  /** ERP buyer code on the PO line. */
  buyer_code: string | null;
  /** ERP warehouse code on the PO line. */
  warehouse_code: string | null;
  schedule_date: string;
  days_overdue: number;
  /** Helix slot status. */
  status: string;
  schedule_qty: number;
  /** Helix received qty (GRN attribution). */
  shipped_qty: number;
  /** Helix balance qty. */
  balance_qty: number;
  erp_balance_qty: number | null;
  /** The ERP reports this line short (it is on the ERP's own shortage report). */
  on_erp_report: boolean;
  /** Helix balance − ERP balance. */
  difference: number;
  alignment: ErpShortageAlignment;
  /** Helix differs from the ERP and nobody has accepted the difference. */
  needs_repair: boolean;
  exception: ErpShortageException | null;
  /** Latest GRN receipt date on the PO line. */
  last_receipt_date: string | null;
  /** A GRN reached Helix after the ERP last reported this line. */
  awaiting_erp: boolean;
  /** Set when the line is half of a next-day ERP duplicate pair (only for callers who may fix them). */
  next_day_duplicate: ErpNextDayDuplicateMark | null;
}

export interface ErpShortagePush {
  available: boolean;
  completed_at?: string;
  /** The middleware's push time, as sent. */
  push_time?: string | null;
  rows?: number;
  slots?: number;
  age_minutes?: number;
  /** No complete push for over three hours. */
  stale?: boolean;
}

export interface ErpShortageSummary {
  /** Every line on the report across the other filters (before the view). */
  lines: number;
  /** Lines the ERP reports short. */
  erp_shortage_lines: number;
  erp_short_qty: number;
  po_lines: number;
  suppliers: number;
  needs_repair: number;
  by_alignment: Record<ErpShortageAlignment, number>;
  /** Next-day ERP duplicate pairs across the other filters; null when the caller may not fix them. */
  next_day_duplicate_pairs: number | null;
}

export interface ErpShortageFacets {
  suppliers: { id: number; name: string; code: string; count: number }[];
  buyers: { code: string; count: number }[];
  warehouses: { code: string; count: number }[];
  /** ERP order types of the POs in the report. */
  order_types: { code: string; count: number }[];
}

export interface ErpShortageResponse {
  enabled: boolean;
  /** Server's cut-off date: lines due before it are overdue. */
  today?: string;
  push: ErpShortagePush;
  /** The caller may accept or revoke differences. */
  can_accept_differences?: boolean;
  /** The caller may fix next-day ERP duplicates (GRN Repair access and import admin). */
  can_fix_duplicates?: boolean;
  /** Breakdown across the other filters, before the view; null when not enabled or no push yet. */
  summary: ErpShortageSummary | null;
  /** Options in the report before the narrowing filters. */
  facets: ErpShortageFacets;
  count: number;
  page: number;
  page_size: number;
  results: ErpShortageRow[];
}

export interface ErpShortageFilters {
  view?: ErpShortageView;
  search?: string;
  supplierId?: number;
  buyer?: string;
  warehouse?: string;
  /** ERP order type of the PO (e.g. '1', '4'). */
  orderType?: string;
  overdue?: ErpShortageOverdue;
  ordering?: ErpShortageOrdering;
  /** Viewer's local date (yyyy-mm-dd). */
  today?: string;
}

/** One next-day ERP duplicate pair, as listed for confirmation. */
export interface ErpNextDayDuplicatePair {
  earlier_schedule_id: number;
  next_day_schedule_id: number;
  po_line_id: number;
  po_number: string;
  line_number: number;
  part_number: string | null;
  supplier_name: string | null;
  supplier_code: string | null;
  qty: string;
  /** Helix received it; the ERP still shows it open. Cancelled by the fix. */
  earlier: {
    schedule_line_number: string | null;
    schedule_date: string;
    /** Still in the ERP's latest push; false when the ERP no longer sends this slot. */
    sent_by_erp: boolean;
  };
  /** The ERP booked the receipt here. Receives the receipts in the fix. */
  next_day: { schedule_line_number: string | null; schedule_date: string };
  /** Receipts on the earlier slot that move to the next-day slot. */
  receipts: { grn_number: string; receipt_date: string; qty: string }[];
}

export interface ErpNextDayDuplicatesResponse {
  count: number;
  pairs: ErpNextDayDuplicatePair[];
}

export interface ErpNextDayDuplicateFixResult {
  fixed: { earlier_schedule_id: number; next_day_schedule_id: number; label: string | null }[];
  skipped: { earlier_schedule_id: number; label: string | null; reason: string }[];
}

function toParams(customerId: number, filters: ErpShortageFilters): Record<string, string | number> {
  const params: Record<string, string | number> = { customer_id: customerId };
  if (filters.view) params['view'] = filters.view;
  if (filters.search) params['search'] = filters.search;
  if (filters.supplierId) params['supplier_id'] = filters.supplierId;
  if (filters.buyer) params['buyer'] = filters.buyer;
  if (filters.warehouse) params['warehouse'] = filters.warehouse;
  if (filters.orderType) params['order_type'] = filters.orderType;
  if (filters.overdue) params['overdue'] = filters.overdue;
  if (filters.ordering) params['ordering'] = filters.ordering;
  if (filters.today) params['today'] = filters.today;
  return params;
}

export const erpShortageAPI = {
  /** One page of the report plus summary, facets and push freshness. */
  list: (
    customerId: number,
    filters: ErpShortageFilters,
    page: number,
    pageSize: number,
  ): Promise<ErpShortageResponse> =>
    api.get<ErpShortageResponse>('/imports/erp-shortage-report/', {
      params: { ...toParams(customerId, filters), page, page_size: pageSize },
    }).then(r => r.data),

  /** Every line on the report for the filters and view, as an .xlsx blob. */
  exportXlsx: (customerId: number, filters: ErpShortageFilters): Promise<Blob> =>
    api.get('/imports/erp-shortage-report/export/', {
      params: toParams(customerId, filters),
      responseType: 'blob',
    }).then(r => r.data as Blob),

  /** Accept the line's current difference as correct (reason required). */
  acceptDifference: (scheduleId: number, reason: string): Promise<void> =>
    api.post('/imports/erp-shortage-report/exceptions/', { schedule_id: scheduleId, reason }).then(() => undefined),

  /** Next-day ERP duplicate pairs for the filters (the view filter is ignored). */
  nextDayDuplicates: (customerId: number, filters: ErpShortageFilters): Promise<ErpNextDayDuplicatesResponse> =>
    api.get<ErpNextDayDuplicatesResponse>('/imports/erp-shortage-report/next-day-duplicates/', {
      params: toParams(customerId, { ...filters, view: undefined }),
    }).then(r => r.data),

  /**
   * Fix pairs by their earlier slot: receipts move to the next-day slot, the
   * earlier slot is cancelled and its ERP-only balance accepted as an ERP duplicate.
   */
  fixNextDayDuplicates: (
    customerId: number,
    earlierScheduleIds: number[],
    today: string,
  ): Promise<ErpNextDayDuplicateFixResult> =>
    api.post<ErpNextDayDuplicateFixResult>('/imports/erp-shortage-report/next-day-duplicates/',
      { earlier_schedule_ids: earlierScheduleIds },
      { params: { customer_id: customerId, today } }).then(r => r.data),

  /** Withdraw an accepted difference; the line needs repair again. */
  revokeDifference: (scheduleId: number): Promise<void> =>
    api.delete('/imports/erp-shortage-report/exceptions/', { params: { schedule_id: scheduleId } }).then(() => undefined),
};
